import type {
  ColDef,
  ColGroupDef,
  Column,
  ColumnGroup,
  ITooltipParams,
  ValueFormatterParams,
} from 'ag-grid-community'

import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  labelAssignments,
  sameAssignments,
} from '@sportninja/common/features/game/gameListView/officialBuckets'
import type {
  OfficialAssignment,
  OfficialBucket,
} from '@sportninja/common/features/game/gameListView/officialBuckets'
import { isGameStarted } from '@sportninja/common/features/game/gameForm/helpers/gameFormMapping'
import type {
  TeamRef,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'
import { formatVenueLocal } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'

import { DateCellEditor, TimeCellEditor } from '../components/editors'
import GameLinkCell from '../components/GameLinkCell'
import GameEditCell from '../components/GameEditCell'
import GameStatusCell from '../components/GameStatusCell'
import OfficialsCellEditor from '../components/OfficialsCellEditor'
import type { OfficialTypeOption } from '../components/OfficialsCellEditor'
import TeamCellEditor from '../components/TeamCellEditor'
import VenueCellEditor from '../components/VenueCellEditor'
import type { OfficialOption } from './useOfficialOptions'
import type { GameGridRow } from '../utils/gameGridRows'
import { compareGameNumber, compareLocalTime } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'

export interface GameTypeOption {
  id: string | number
  name?: string
  name_full?: string
}

export interface UseDetailsColumnsArgs {
  canEdit: boolean
  venuesById: Map<string, VenueRef>
  /** Every selectable team, keyed by uid — unioned with the ones on the rows. */
  teamsById: Map<string, TeamRef>
  /** True while the team pool is in flight, so the editor can say so. */
  teamsLoading: boolean
  gameTypes: GameTypeOption[]
  /** Label the TIME column with the zone when rows span more than one. */
  showTimeZoneInHeader: boolean
  /** Rows with a PUT in flight cannot start another edit. */
  isRowPending: (row: GameGridRow) => boolean
  /** Every assignable official, unioned with the ones already on loaded games. */
  officials: OfficialOption[]
  /** uid → display name. Flat and row-independent — see `facilityNamesById`. */
  officialNamesById: Map<string, string>
  /** Every official role, from Redux. Never filtered by sport — see below. */
  officialTypes: OfficialTypeOption[]
  /**
   * False where the officials pool cannot be loaded — the Team and My Schedule
   * hosts have no organization to scope the search to. The columns still SHOW
   * what the payload carries; they just cannot be edited there.
   */
  canAssignOfficials: boolean
}

const UNASSIGNED = '__unassigned__'

/**
 * Every option column stores an ID and renders a label through a formatter.
 *
 * Storing the label instead would force a reverse lookup on commit, and this
 * data has genuine duplicates — two "Rink 1"s at different venues, two divisions
 * called "U11 A" in different seasons, teams sharing a name across conferences.
 * A name-to-id lookup would silently write the wrong one.
 */
/** Separates a column's own tooltip from the edit affordance appended to it. */
const EDIT_HINT_SEPARATOR = ' \u2014 '

/**
 * `ITooltipParams.column` is a `Column` OR a `ColumnGroup`; only the former
 * knows about editability.
 */
const isColumn = (column: Column | ColumnGroup | undefined): column is Column =>
  column !== undefined && 'isCellEditable' in column

/**
 * Appends "double-click to edit" to a column's tooltip, on the rows where the
 * cell really is editable.
 *
 * Applied to every column through `pick` rather than written into each colDef,
 * so a new editable column cannot ship without the affordance and the wording
 * stays in one place. Read-only columns fall straight through untouched.
 *
 * COMPOSED with whatever tooltip the column already had rather than replacing
 * it: Location and Surface store an id and resolve it to a label, and trading
 * that label for a gesture hint would be a downgrade.
 *
 * Editability is asked of AG GRID (`Column.isCellEditable`) instead of being
 * re-derived here. That runs the column's own `editable` callback against the
 * row, so the two cases that turn editing off — a row with a PUT in flight, and
 * a surface with no location chosen yet — keep exactly one definition, and a
 * read-only viewer (`canEdit: false`) is told nothing to double-click.
 */
const withEditHint = (column: ColDef<GameGridRow>): ColDef<GameGridRow> => {
  if (column.editable === undefined || column.editable === false) {
    return column
  }

  const baseTooltip = column.tooltipValueGetter

  return {
    ...column,
    tooltipValueGetter: (params: ITooltipParams<GameGridRow>) => {
      const base = baseTooltip?.(params) ?? null
      const canEditCell =
        params.node !== undefined &&
        isColumn(params.column) &&
        params.column.isCellEditable(params.node)

      if (!canEditCell) {
        return base
      }

      const hint = gameViewsLabel('gameGridEditHint')
      return base == null || base === ''
        ? hint
        : `${base}${EDIT_HINT_SEPARATOR}${hint}`
    },
  }
}

/**
 * Turns the change flash ON, but only where a change can actually be seen.
 *
 * This used to live on `defaultColDef`, which is what made a single-cell edit
 * light up the whole left-hand gutter: `openGame`, `editGame` and AG Grid's own
 * selection column carry no `field` and no `valueGetter`, and a column with no
 * value takes the `noValueProvided` branch in `refreshCell` — `forceRefresh`,
 * so the cell flashes on EVERY row-data replacement whether or not anything
 * about it changed. The grid hands React a fresh row object on each render, so
 * that was every render.
 *
 * Opting IN per column rather than opting the gutter out, because the selection
 * column cannot be opted out: `SelectionColumnDef` is a `Pick` of `ColDef` that
 * does not include this flag. Nothing can inherit what nothing sets.
 *
 * Applied through `pick`, so a new data column gets the flash for free and a
 * new chrome column cannot accidentally acquire it.
 */
const withChangeFlash = (column: ColDef<GameGridRow>): ColDef<GameGridRow> =>
  column.field === undefined && column.valueGetter === undefined
    ? column
    : { ...column, enableCellChangeFlash: true }

/**
 * Title-cases a column's header through the shared helper.
 *
 * Headers render as words now rather than as ALL CAPS, so the casing in the
 * translation is what the admin reads — "Game type" beside "Game #" is the kind
 * of drift the helper exists to absorb. Applied through `pick`, so a new column
 * cannot ship without it.
 */
const withTitleCase = (column: ColDef<GameGridRow>): ColDef<GameGridRow> => ({
  ...column,
  headerName: capitalizeWords(column.headerName),
})

export const useDetailsColumns = ({
  canEdit,
  venuesById,
  teamsById,
  teamsLoading,
  gameTypes,
  showTimeZoneInHeader,
  isRowPending,
  officials,
  officialNamesById,
  officialTypes,
  canAssignOfficials,
}: UseDetailsColumnsArgs): (
  | ColDef<GameGridRow>
  | ColGroupDef<GameGridRow>
)[] => {
  'use memo'

  const venueLabel = (id: string | null | undefined): string => {
    if (id == null || id === '') {
      return ''
    }
    const venue = venuesById.get(id)
    return venue?.name_full ?? venue?.name ?? ''
  }

  /**
   * Every surface on every location, by id.
   *
   * Flat and row-independent on purpose. The column's `valueFormatter` labels
   * the dropdown OPTIONS as well as the cell, and AG Grid hands it no row when
   * it is formatting a list item — so a lookup scoped to `data`'s venue finds
   * nothing and falls back to "Unassigned" for every option.
   *
   * id → name is safe even though this data has duplicate NAMES (two "Rink 1"s
   * at different locations): an id belongs to exactly one surface. It is the
   * reverse direction, name → id, that would be a coin flip — which is why the
   * editor stores ids and only ever renders through here.
   */
  const facilityNamesById = new Map<string, string>()
  venuesById.forEach((venue) => {
    const facilities = venue.facilities ?? []
    facilities.forEach((facility) => {
      //  A nameless surface is indistinguishable from no surface to a reader,
      //  so it falls through to the same label rather than rendering blank.
      if (facility.name != null && facility.name !== '') {
        facilityNamesById.set(facility.id, facility.name)
      }
    })
  })

  const facilityLabelFor = (id: string | null | undefined): string => {
    if (id == null || id === UNASSIGNED) {
      return gameViewsLabel('gameGridUnassigned')
    }
    return facilityNamesById.get(id) ?? gameViewsLabel('gameGridUnassigned')
  }

  const facilityLabel = (row: GameGridRow | undefined): string =>
    facilityLabelFor(row?.facilityId)

  const gameTypeLabel = (id: number | null): string => {
    if (id == null) {
      return ''
    }
    const match = gameTypes.find((type) => Number(type.id) === id)
    //  `name` is the display label ("Regular Season"); `name_full` is the enum
    //  key ("REGULAR_SEASON") that mirrors the `GameType::` constants and was
    //  never meant to reach a user. The naming reads backwards — the same trap
    //  the rest of the app avoids by rendering `name` (see Form/GameTypeSelect).
    return match?.name ?? ''
  }

  const editable = (row?: GameGridRow) =>
    canEdit && row !== undefined && !isRowPending(row)

  /**
   * Teams are editable until the game starts.
   *
   * From the first whistle its stats, rosters and timeline events are attached
   * to the two sides, and swapping one would leave that history pointing at a
   * team that never played. `isGameStarted` is the modal's own gate, and it
   * reads the raw timestamps precisely because the grid supplies the LIST
   * resource rather than a parsed entity.
   */
  const teamEditable = (row?: GameGridRow) =>
    editable(row) && !isGameStarted(row?.game)

  /**
   * uid → team name, flat and row-independent.
   *
   * Same shape as `facilityNamesById` and for a related reason: a team can be
   * missing from the pool entirely (it plays in the competition but is not
   * returned by `/teams/simple`), and rendering a bare uid in that case is the
   * regression to avoid. The row's own label is the fallback.
   */
  const teamLabel = (
    id: string | null | undefined,
    fallback: string
  ): string => {
    if (id == null || id === '') {
      return fallback
    }
    const team = teamsById.get(id)
    return team?.name_full ?? team?.name ?? fallback
  }

  /*
   * Role labels, by id. Flat and row-independent for the same reason
   * `facilityNamesById` is: `labelAssignments` runs inside a column
   * `valueFormatter`, which AG Grid hands no row.
   *
   * NOT filtered by the competition's sport. Every `official_type` row in the
   * database is `sport_id = 1`, yet soccer, ball hockey and generic games hold
   * tens of thousands of assignments between them — a sport filter renders an
   * empty role list for every non-hockey competition. The Redux selector that
   * filters by sport is deprecated for exactly this reason.
   */
  const officialRoleNamesById = new Map<number, string>()
  //  Defensive: the roles come from a Redux slice that is populated by a
  //  separate fetch, and a grid that renders no columns at all is a far worse
  //  failure than one whose role labels are briefly missing.
  ;(officialTypes ?? []).forEach((type) => {
    officialRoleNamesById.set(Number(type.id), type.name)
  })

  const officialsLabel = (
    bucket: OfficialBucket,
    assignments: OfficialAssignment[] | null | undefined
  ): string =>
    labelAssignments(assignments, {
      bucket,
      nameFor: (officialId) => officialNamesById.get(officialId) ?? officialId,
      roleFor: (typeId) => officialRoleNamesById.get(typeId) ?? '',
    })

  /**
   * One officials column.
   *
   * The three are identical apart from which roles they own, so they are built
   * from one definition — a hand-written triple is three chances for the
   * formatter, the filter and the tooltip to drift apart.
   */
  /**
   * One side of a game: Home or Visitor.
   *
   * A factory rather than two literals — the pair differs only in which field
   * it reads, and the parts that are easy to get wrong (the label fallback for
   * a bracket slot, the started-game gate, routing sort and search back to the
   * label) are exactly the parts that must not drift between them.
   */
  const teamColumn = (
    colId: 'homeLabel' | 'awayLabel',
    idField: 'homeTeamId' | 'visitingTeamId',
    labelKey: string
  ): ColDef<GameGridRow> => {
    //  A bracket-slot game has no team on this side at all, so the row's label
    //  carries the SLOT's name (or TBD) and is the only thing worth showing.
    const label = (row?: GameGridRow) =>
      teamLabel(row?.[idField], row?.[colId] ?? '')

    return {
      colId,
      field: idField,
      headerName: gameViewsLabel(labelKey),
      minWidth: 160,
      flex: 1.4,
      editable: (params) => teamEditable(params.data),
      cellEditor: TeamCellEditor,
      cellEditorParams: { teamsById, teamsLoading },
      //  A popup: the option list is far taller than the 48px row it opens from.
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      //  Reads the ROW, not just the value — safe here in a way it would not be
      //  on an `agSelectCellEditor` column, which asks the same formatter to
      //  label its options and hands it no row when it does (v36 dropped
      //  `formatValue`). This editor labels its own options.
      valueFormatter: ({ data }: ValueFormatterParams<GameGridRow>) =>
        label(data),
      /*
       * The tooltip does two jobs.
       *
       * Team names truncate long before a division name does, so it is the only
       * way to read "Burnaby Winter Club U13 A2" in a 160px cell. And on a game
       * that has already started it also says WHY the cell will not open — a
       * whole played season is locked, and a cell that silently refuses to edit
       * reads as broken rather than as a rule. Same reasoning as the bulk bar
       * putting its reason on every disabled action.
       *
       * Only for someone who could otherwise edit: a read-only viewer is not
       * owed an explanation for an edit they were never offered.
       */
      tooltipValueGetter: ({ data }: { data?: GameGridRow }) => {
        const base = label(data)
        if (!canEdit || !isGameStarted(data?.game)) {
          return base
        }
        const reason = gameViewsLabel('gameGridTeamLockedStarted')
        return base === '' ? reason : `${base}${EDIT_HINT_SEPARATOR}${reason}`
      },
      //  The quick filter reads column text through `filterValueSvc`, which
      //  honours this — without it the toolbar search would be matching uids.
      filterValueGetter: ({ data }: { data?: GameGridRow }) => label(data),
      //  Sorts on the displayed name. The default comparator would order the
      //  column by opaque uid, which looks like no sort at all.
      comparator: (_a: string, _b: string, nodeA, nodeB) =>
        label(nodeA?.data as GameGridRow).localeCompare(
          label(nodeB?.data as GameGridRow)
        ),
    }
  }

  const officialsColumn = (
    bucket: OfficialBucket,
    labelKey: string
  ): ColDef<GameGridRow> => ({
    colId: bucket,
    field: bucket,
    headerName: gameViewsLabel(labelKey),
    minWidth: 190,
    flex: 1.4,
    /*
     * This cell holds a LIST OF PAIRS, not a scalar.
     *
     * Left to itself AG Grid infers a data type from the first value and attaches
     * the matching parser and formatter (warning #48). For an array that
     * inference is meaningless, and the inferred parser would sit between the
     * editor's commit and `onCellValueChanged` — free to mangle the array on its
     * way through. Opting out keeps the value exactly as the editor produced it.
     */
    cellDataType: false,
    /*
     * Set equality over (person, role) pairs, because the default is `===` and
     * this value is a FRESH ARRAY on every render — `assignmentsForBucket`
     * builds one per call. Against `defaultColDef`'s change flash that meant
     * all three columns lit up on every unrelated row-data replacement, having
     * changed nothing.
     *
     * `sameAssignments` and not `reconcile`'s key function: this value is the
     * grid's `OfficialAssignment` (`officialId` / `officialTypeId`), while
     * reconcile works in the API's `id` / `official_type_id` shape.
     */
    equals: sameAssignments,
    //  Editable only where the pool can be fetched. Elsewhere the cell still
    //  renders what the game payload carries — read-only is honest, blank is not.
    editable: (params) => editable(params.data) && canAssignOfficials,
    cellEditor: OfficialsCellEditor,
    cellEditorParams: {
      bucket,
      officials,
      officialNamesById,
      officialTypes,
      columnLabel: capitalizeWords(gameViewsLabel(labelKey)),
    },
    //  A popup: the panel stacks a multi-select over one row per person, which
    //  is far taller than the 48px row it opens from.
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
    //  Formats the VALUE it is handed and never the row — see the note on the
    //  surface column about v36 dropping `formatValue`.
    valueFormatter: ({ value }: ValueFormatterParams<GameGridRow>) =>
      officialsLabel(bucket, value as OfficialAssignment[] | null),
    //  Names, not uids: the toolbar search has to match what the cell shows.
    filterValueGetter: ({ data }: { data?: GameGridRow }) =>
      officialsLabel(bucket, data?.[bucket]),
    tooltipValueGetter: ({ data }: { data?: GameGridRow }) =>
      officialsLabel(bucket, data?.[bucket]),
    comparator: (
      _a: OfficialAssignment[],
      _b: OfficialAssignment[],
      nodeA,
      nodeB
    ) =>
      officialsLabel(
        bucket,
        (nodeA?.data as GameGridRow)?.[bucket]
      ).localeCompare(
        officialsLabel(bucket, (nodeB?.data as GameGridRow)?.[bucket])
      ),
  })

  const columns: ColDef<GameGridRow>[] = [
    {
      colId: 'localDate',
      field: 'localDate',
      headerName: gameViewsLabel('gameGridColDate'),
      //  Wide enough for the longest `EEE, MMM d, yyyy` render
      //  ("Wed, Sep 10, 2026") at the grid's 14px with its 14px side padding.
      //  The columns share the width by `flex`, so a date column that can be
      //  squeezed to its minimum IS squeezed there on a full-width grid — and
      //  a truncated date is the one thing this view exists to show.
      minWidth: 178,
      flex: 1,
      editable: (params) => editable(params.data),
      cellEditor: DateCellEditor,
      //  Sort on the canonical string; a localized label would sort by its
      //  first character, putting December before February.
      comparator: (a: string, b: string) => (a === b ? 0 : a < b ? -1 : 1),
      //  Filter on the LABEL, not the canonical value — typing "Sep" into the
      //  filter row has to match what the column is showing, and the raw value
      //  is `2026-09-08`.
      filterValueGetter: ({ data }: { data?: GameGridRow }) =>
        data === undefined
          ? ''
          : formatVenueLocal(
              data.game.starts_at,
              data.timeZone,
              'EEE, MMM d, yyyy'
            ),
      valueFormatter: ({ data }: ValueFormatterParams<GameGridRow>) =>
        data?.localDate
          ? formatVenueLocal(
              data.game.starts_at,
              data.timeZone,
              'EEE, MMM d, yyyy'
            )
          : '',
    },
    {
      colId: 'localTime',
      field: 'localTime',
      headerName: showTimeZoneInHeader
        ? gameViewsLabel('gameGridColTimeVenueLocal')
        : gameViewsLabel('gameGridColTime'),
      //  Wider than the other short columns because the EDITOR is wider than
      //  the value: the cell has to hold the time field and the TBD switch side
      //  by side. `flex` means this only binds on a cramped grid, which is
      //  exactly where a clipped switch would be unusable.
      minWidth: 160,
      flex: 1,
      editable: (params) => editable(params.data),
      cellEditor: TimeCellEditor,
      comparator: compareLocalTime,
      filterValueGetter: ({ data }: { data?: GameGridRow }) => {
        if (data === undefined) {
          return ''
        }
        return data.isTbd
          ? gameViewsLabel('gameGridTbd')
          : formatVenueLocal(data.game.starts_at, data.timeZone, 'h:mm a')
      },
      valueFormatter: ({ data }: ValueFormatterParams<GameGridRow>) => {
        if (data === undefined) {
          return ''
        }
        //  Never render a fake time for TBD — the sentinel would read as 23:58.
        if (data.isTbd) {
          return gameViewsLabel('gameGridTbd')
        }
        return formatVenueLocal(data.game.starts_at, data.timeZone, 'h:mm a')
      },
    },
    /*
     * The two sides, editable.
     *
     * Both hold the team UID and render the name through a `valueFormatter`,
     * the same way every other option column here works: team names repeat
     * across conferences, so a name-to-id lookup would silently pick one of
     * them. Everything the user reads, sorts and searches on therefore has to
     * be routed back to the LABEL explicitly — a uid in a search box or a sort
     * is worse than no feature at all.
     *
     * `colId` stays `homeLabel` / `awayLabel`: it is the identity AG Grid
     * persists column state under, and renaming it would silently reset
     * everyone's saved widths and visibility.
     */
    teamColumn('homeLabel', 'homeTeamId', 'gameGridColHome'),
    teamColumn('awayLabel', 'visitingTeamId', 'gameGridColVisitor'),
    {
      colId: 'divisionLabel',
      field: 'divisionLabel',
      headerName: gameViewsLabel('gameGridColDivision'),
      minWidth: 150,
      flex: 1.2,
      tooltipField: 'divisionLabel',
      editable: false,
    },
    {
      colId: 'venueId',
      field: 'venueId',
      headerName: gameViewsLabel('gameGridColVenue'),
      minWidth: 180,
      flex: 1.4,
      editable: (params) => editable(params.data),
      //  Location and surface commit as a PAIR. A surface belongs to one
      //  building, so a location change must replace or clear it in the same
      //  request — see `VenueCellEditor`.
      cellEditor: VenueCellEditor,
      cellEditorParams: { venuesById },
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      valueFormatter: ({ value }: ValueFormatterParams<GameGridRow>) =>
        venueLabel(value as string),
      comparator: (a: string, b: string) =>
        venueLabel(a).localeCompare(venueLabel(b)),
      //  A uid is not something anyone can type into a filter box.
      filterValueGetter: ({ data }: { data?: GameGridRow }) =>
        venueLabel(data?.venueId),
      //  `tooltipField` would show the raw uid — this column stores an id and
      //  renders a label, so the tooltip has to resolve it too.
      tooltipValueGetter: ({ data }: { data?: GameGridRow }) =>
        venueLabel(data?.venueId),
    },
    {
      colId: 'facilityId',
      field: 'facilityId',
      headerName: gameViewsLabel('gameGridColFacility'),
      minWidth: 150,
      flex: 1,
      editable: (params) =>
        editable(params.data) && Boolean(params.data?.venueId),
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: (params: { data?: GameGridRow }) => {
        const venue = params.data?.venueId
          ? venuesById.get(params.data.venueId)
          : undefined
        //  The update endpoint refuses a move onto an inactive surface, so
        //  offering one only produces a rejection nobody can act on. The
        //  currently-assigned surface stays listed so the field can show it.
        const facilities = (venue?.facilities ?? []).filter(
          (facility) =>
            facility.is_active !== false ||
            facility.id === params.data?.facilityId
        )
        //  Ids only. `formatValue` was dropped from `ISelectCellEditorParams`
        //  in v36 — the select editor labels its options with the COLUMN's
        //  `valueFormatter`, so that is the one place labelling can live.
        /*
         * NO "Unassigned" option. Clearing a surface is not an edit this API
         * can express — a submitted null `facility_id` is dropped — so offering
         * it could only ever produce a row that reads Unassigned while nothing
         * was saved. A game with no surface still RENDERS as Unassigned through
         * the value formatter; it simply cannot be moved back to that state.
         */
        return {
          values: facilities.map((facility) => facility.id),
        }
      },
      //  Labels the option as well as the cell, so it must format the VALUE it
      //  is handed and never the row's own facility.
      valueFormatter: ({ value }: ValueFormatterParams<GameGridRow>) =>
        facilityLabelFor(value as string | null),
      filterValueGetter: ({ data }: { data?: GameGridRow }) =>
        facilityLabel(data),
      tooltipValueGetter: ({ data }: { data?: GameGridRow }) =>
        facilityLabel(data),
      comparator: (_a: string, _b: string, nodeA, nodeB) =>
        facilityLabel(nodeA?.data as GameGridRow).localeCompare(
          facilityLabel(nodeB?.data as GameGridRow)
        ),
    },
    {
      colId: 'gameNumber',
      field: 'gameNumber',
      headerName: gameViewsLabel('gameGridColGameNumber'),
      minWidth: 120,
      flex: 0.8,
      //  Right-aligned, header and cell, the way a spreadsheet aligns a number.
      //  AG Grid's own column type rather than a hand-rolled class: it carries
      //  the header with the cell and gets RTL right for free.
      type: 'numericColumn',
      editable: (params) => editable(params.data),
      comparator: compareGameNumber,
    },
    {
      colId: 'gameTypeId',
      field: 'gameTypeId',
      headerName: gameViewsLabel('gameGridColGameType'),
      minWidth: 140,
      flex: 1,
      editable: (params) => editable(params.data),
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: gameTypes.map((type) => Number(type.id)) },
      valueFormatter: ({ value }: ValueFormatterParams<GameGridRow>) =>
        gameTypeLabel(value as number | null),
      filterValueGetter: ({ data }: { data?: GameGridRow }) =>
        gameTypeLabel(data?.gameTypeId ?? null),
      comparator: (a: number | null, b: number | null) =>
        gameTypeLabel(a).localeCompare(gameTypeLabel(b)),
    },
    {
      colId: 'status',
      field: 'statusLabel',
      headerName: gameViewsLabel('gameGridColStatus'),
      minWidth: 140,
      flex: 1,
      //  Derived from the game, never typed: a status is set through the game's
      //  own status modal, where cancelling or forfeiting has consequences a
      //  cell edit could not carry.
      editable: false,
      cellRenderer: GameStatusCell,
      //  Ordered by what HAPPENED — scheduled, live, final, then the exceptions
      //  — rather than alphabetically, which files Cancelled above Final.
      comparator: (
        _a: string,
        _b: string,
        nodeA: { data?: GameGridRow },
        nodeB: { data?: GameGridRow }
      ) => (nodeA?.data?.status.rank ?? 0) - (nodeB?.data?.status.rank ?? 0),
    },
    {
      colId: 'scoreLabel',
      field: 'scoreLabel',
      headerName: gameViewsLabel('gameGridColScore'),
      minWidth: 100,
      flex: 0.7,
      //  Scores belong to scoring, not scheduling. Unsortable too: a column of
      //  mostly-blank strings sorts into noise and invites the wrong edit.
      editable: false,
      sortable: false,
    },
    officialsColumn('referees', 'gameGridColReferees'),
    officialsColumn('scorekeepers', 'gameGridColScorekeepers'),
    officialsColumn('timekeepers', 'gameGridColTimekeepers'),
    {
      colId: 'openGame',
      //  Headerless, like the selection checkbox it sits beside: this is the
      //  row's leading gutter — controls for the row, not a field of the game —
      //  and a lone word floating over a 56px icon column reads as a stray
      //  column instead. The icon carries a tooltip and an accessible name.
      headerName: '',
      cellRenderer: GameLinkCell,
      //  PINNED LEFT, immediately after the checkbox: the sheet is wider than
      //  most screens, and a link you have to scroll sideways to find is the
      //  bug this column exists to fix.
      pinned: 'left',
      //  Tight against the checkbox: the two are one gutter of row controls, and
      //  a wide column between them reads as a gap rather than a pair. The cell
      //  padding is dropped in `DetailsGrid` so the icon centres in this width.
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      resizable: false,
      cellClass: 'sn-cell-open-game',
      //  Chrome, not data: nothing to edit, sort, search or drag, and it holds
      //  no value a filter could match. `suppressColumnsToolPanel` is also what
      //  keeps it out of the Columns chooser and the CSV — see `useGridToolbar`.
      //  Never editable, and deliberately independent of `canEdit`: a read-only
      //  viewer has the same reason to open a game as an admin does.
      editable: false,
      sortable: false,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
    },
    /*
     * Only for admins. Dropped entirely rather than rendered disabled: unlike
     * the link beside it, which a read-only viewer has every reason to use,
     * a permanently inert edit button is just noise in a 40px gutter.
     */
    ...(canEdit
      ? [
          {
            colId: 'editGame',
            //  Headerless for the same reason as `openGame` — the two form one
            //  gutter of row controls, not a pair of data columns.
            headerName: '',
            cellRenderer: GameEditCell,
            pinned: 'left' as const,
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            resizable: false,
            cellClass: 'sn-cell-open-game',
            //  Chrome, not data: same exclusions as `openGame`, so it stays out
            //  of the Columns chooser and the CSV export.
            editable: false,
            sortable: false,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
          },
        ]
      : []),
  ]

  const pick = (...ids: string[]): ColDef<GameGridRow>[] =>
    ids
      .map((id) => columns.find((column) => column.colId === id))
      .filter((column): column is ColDef<GameGridRow> => column !== undefined)
      .map(withTitleCase)
      .map(withEditHint)
      .map(withChangeFlash)

  const group = (
    labelKey: string,
    ...ids: string[]
  ): ColGroupDef<GameGridRow> => ({
    headerName: capitalizeWords(gameViewsLabel(labelKey)),
    groupId: labelKey,
    children: pick(...ids),
  })

  /**
   * Grouped headers, as the reference grid does it. Eleven columns of date,
   * team, place and paperwork read as one undifferentiated wall; a spanning
   * header per concern costs a single 44px row and turns the sheet into four
   * things you can aim at.
   */
  /*
   * NO row-number gutter. A column over `node.rowIndex` is how you rebuild
   * Enterprise's `rowNumbers` on Community, and it was here — but it put a
   * second numeric '#' four columns from the real GAME #, which reads as the
   * same fact and disagrees with it (1 against 781). A screen position nobody
   * cites is not worth that ambiguity; selection is what the checkbox column
   * is for.
   */
  return [
    //  First, and outside the groups: they belong to no concern the header row
    //  names, and they are part of the row's leading gutter with the checkbox.
    //  `editGame` is absent from the pool entirely for a read-only viewer, and
    //  `pick` drops ids it cannot find, so this needs no second `canEdit` gate.
    ...pick('openGame', 'editGame'),
    group('gameGridGroupWhen', 'localDate', 'localTime'),
    group('gameGridGroupTeams', 'homeLabel', 'awayLabel', 'divisionLabel'),
    group('gameGridGroupWhere', 'venueId', 'facilityId'),
    group(
      'gameGridGroupGame',
      'gameNumber',
      'gameTypeId',
      'status',
      'scoreLabel'
    ),
    //  Its own group rather than folded into GAME: these are the people who RUN
    //  the game, not properties of it, and the three read as a set.
    group('gameGridGroupOfficials', 'referees', 'scorekeepers', 'timekeepers'),
  ]
}

export const FACILITY_UNASSIGNED_VALUE = UNASSIGNED
